// Lib
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { length } from '../../../../../../../common/utils/immutableHelper';
import { getColorDisplayValue } from '../../../../../../../common/colors/elementColorFormatUtil';
import { getThemeIsDarkMode } from '../../../../../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getBackgroundColorHex } from '../../../../../../element/table/utils/tableCellFormattingUtils';
import { setHotTableCellBackgroundColor } from '../../../../../../element/table/utils/hotInstanceUtils/hotTableCellMetaUpdateUtils';
import { useTableActionsSwitch } from '../../../../../../element/table/actions/useTableActionsSwitch';
import {
    selectLegacyActiveTableCellSelection,
    selectLegacyActiveTableCurrentCellBackgroundColor,
} from '../../../../../../element/table/store/selectors/legacyActiveHotTableSelectors';

// Components
import ToolbarPopupTool from '../../../ToolbarPopupTool';
import DesktopColorPopup from '../../colorTools/popup/desktop/DesktopColorPopup';
import ColorToolIcon from '../../colorTools/ColorToolIcon';
import {
    useLegacyActiveTableCallback,
    useLegacyActiveTableSelector,
} from '../../../../../../element/table/store/legacyHotTableStoreHooks';

// Constants
import { PopupIds, PopupSections } from '../../../../../../components/popupPanel/popupConstants';
import {
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../../../../common/table/tableConstants';
import { CellSelections } from '../../../../../../../common/table/TableTypes';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';

// Styles
import '../TableCellColorTool.scss';

type ColorToolPopupIconProps = {
    selectedColor: string | null | undefined;
    isPopupOpen: boolean;
    currentCellSelections: CellSelections;
};

const ColorToolPopupIcon: React.FC<ColorToolPopupIconProps> = ({
    selectedColor,
    isPopupOpen,
    currentCellSelections,
}) => {
    const color =
        currentCellSelections?.length > 0
            ? getColorDisplayValue(selectedColor)
            : 'var(--ui-toolbar-tool-disabled-foreground)';
    const selectedColorDisplayValue =
        selectedColor === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR ? 'var(--ws-table-header-row-background)' : color;

    return <ColorToolIcon color={selectedColorDisplayValue} isActive={isPopupOpen} />;
};

type LegacyTableCellColorToolProps = {
    element?: ImMNElement;
};

const LegacyTableCellColorTool: React.FC<LegacyTableCellColorToolProps> = (props) => {
    const { element } = props;

    const isDarkMode = useSelector(getThemeIsDarkMode);
    const currentCellSelections = useLegacyActiveTableSelector(selectLegacyActiveTableCellSelection);

    const elementId = getElementId(element);

    const tableActions = useTableActionsSwitch();

    const selectedColor = useLegacyActiveTableSelector(selectLegacyActiveTableCurrentCellBackgroundColor);

    const dispatch = useDispatch();

    const updateCellBackgroundColor = useLegacyActiveTableCallback(
        (activeTable, backgroundColor: string | null) => {
            dispatch(
                tableActions.updateTableCellBackgroundColor({
                    hotInstance: activeTable,
                    elementId,
                    backgroundColor,
                }),
            );
        },
        [elementId, tableActions],
    );

    const updateTemporaryCellColor = useLegacyActiveTableCallback(
        (activeTable, tempColor: string | null) => setHotTableCellBackgroundColor(activeTable, undefined, tempColor),
        [setHotTableCellBackgroundColor],
    );

    return (
        <ToolbarPopupTool
            {...props}
            name="Cell color"
            popupToolClassName="LegacyTableTool"
            buttonSelector=".ColorTool .ColorToolIcon"
            popupId={PopupIds.COLOR}
            closeSection={PopupSections.TOOLBAR}
            toolName="color"
            disabled={length(currentCellSelections) === 0}
            // @ts-ignore Struggling with the JSX type inference
            className="table-cell-color-tool LegacyTableCellColorTool"
            customColorHandler={updateCellBackgroundColor}
            IconComponent={ColorToolPopupIcon}
            PopupComponent={DesktopColorPopup}
            selectedColor={selectedColor}
            selectedColorHex={getBackgroundColorHex(selectedColor, isDarkMode)}
            customDefaultColors={Object.values(TABLE_DEFAULT_COLORS)}
            customTempColorHandler={updateTemporaryCellColor}
            currentCellSelections={currentCellSelections}
            preventFocus
        />
    );
};

export default LegacyTableCellColorTool;
